/**
 * DuplicatesBanner — surfaces same-filename + same-size groups in the
 * current folder and offers one-click cleanup of older copies.
 *
 * Why: each batch upload writes a fresh R2 object even when the same file
 * is re-uploaded, so the storage listing accumulates duplicates that the
 * user would otherwise have to chase one-by-one. This banner detects them
 * client-side (no schema change required) and ships a mass-action.
 *
 * Heuristic: group by `${filename}-${sizeBytes}`. Cheap, false-positive
 * safe (different files with identical name+size are rare; user reviews
 * the modal before deleting). True content-hash dedupe at upload time
 * is tracked separately as a backend follow-up — see issue note in PR.
 */

import { useMemo, useState } from 'react'
import { AlertTriangle, X } from 'lucide-react'
import { useDeleteUpload } from '../../hooks/use-uploads'
import type { StorageFile } from '../../hooks/use-storage'
import { cn } from '../../lib/utils'

interface Group {
  key: string
  filename: string
  sizeBytes: number
  copies: StorageFile[]
}

function detectGroups(files: StorageFile[]): Group[] {
  const map = new Map<string, Group>()
  for (const f of files) {
    const k = `${f.filename}::${f.sizeBytes}`
    const existing = map.get(k)
    if (existing) {
      existing.copies.push(f)
    } else {
      map.set(k, { key: k, filename: f.filename, sizeBytes: f.sizeBytes, copies: [f] })
    }
  }
  // Sort each group's copies newest first so [0] is the keeper.
  for (const g of map.values()) {
    g.copies.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  }
  return Array.from(map.values()).filter((g) => g.copies.length > 1)
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`
}

interface Props {
  files: StorageFile[]
  isDark: boolean
}

export function DuplicatesBanner({ files, isDark }: Props) {
  const groups = useMemo(() => detectGroups(files), [files])
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const deleteUpload = useDeleteUpload()

  if (groups.length === 0) return null

  const totalDupCopies = groups.reduce((sum, g) => sum + (g.copies.length - 1), 0)
  const wastedBytes = groups.reduce(
    (sum, g) => sum + g.sizeBytes * (g.copies.length - 1),
    0,
  )

  async function handleCleanupAll() {
    if (busy) return
    setBusy(true)
    try {
      // Delete every copy beyond the newest of each group.
      const toDelete = groups.flatMap((g) => g.copies.slice(1))
      await Promise.allSettled(
        toDelete.map((f) => deleteUpload.mutateAsync(f.id)),
      )
      setOpen(false)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div
        role="alert"
        className={cn(
          'flex items-center justify-between gap-3 border-b px-4 py-2.5 text-xs',
          isDark
            ? 'border-amber-500/20 bg-amber-500/[0.06] text-amber-200'
            : 'border-amber-200 bg-amber-50 text-amber-800',
        )}
      >
        <div className="flex items-center gap-2">
          <AlertTriangle size={14} aria-hidden="true" className="shrink-0" />
          <span>
            <strong>{totalDupCopies} duplicate {totalDupCopies === 1 ? 'copy' : 'copies'}</strong>
            {' '}across {groups.length} {groups.length === 1 ? 'file' : 'files'} — wasting{' '}
            <strong>{formatBytes(wastedBytes)}</strong>.
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cn(
            'rounded px-2.5 py-1 font-medium transition-colors',
            isDark
              ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-100'
              : 'bg-amber-200 hover:bg-amber-300 text-amber-900',
          )}
        >
          Review &amp; clean up
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="dup-modal-title"
            className={cn(
              'flex w-full max-w-2xl flex-col rounded-lg shadow-xl',
              isDark ? 'bg-surface-1 text-neutral-100' : 'bg-white text-neutral-900',
            )}
          >
            <div className={cn('flex items-center justify-between border-b px-5 py-4', isDark ? 'border-white/[0.06]' : 'border-neutral-200')}>
              <h2 id="dup-modal-title" className="text-base font-semibold">
                Duplicate files
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className={cn(
                  'rounded p-1 transition-colors',
                  isDark ? 'text-neutral-400 hover:bg-surface-3 hover:text-neutral-200' : 'text-neutral-500 hover:bg-neutral-100',
                )}
              >
                <X size={16} />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto px-5 py-4 space-y-3">
              <p className={cn('text-xs', isDark ? 'text-neutral-400' : 'text-neutral-600')}>
                Same filename and size. Newest copy of each is kept; older copies will be deleted.
              </p>
              <ul className="space-y-2">
                {groups.map((g) => (
                  <li
                    key={g.key}
                    className={cn(
                      'rounded-lg border p-3',
                      isDark ? 'border-white/[0.06] bg-surface-2' : 'border-neutral-200 bg-neutral-50',
                    )}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium" title={g.filename}>
                          {g.filename}
                        </p>
                        <p className={cn('text-[11px]', isDark ? 'text-neutral-500' : 'text-neutral-500')}>
                          {formatBytes(g.sizeBytes)} · {g.copies.length} copies · keep newest, delete {g.copies.length - 1}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className={cn('flex items-center justify-end gap-2 border-t px-5 py-3', isDark ? 'border-white/[0.06]' : 'border-neutral-200')}>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  isDark ? 'text-neutral-300 hover:bg-surface-3' : 'text-neutral-600 hover:bg-neutral-100',
                )}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={handleCleanupAll}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm font-medium text-white transition-colors disabled:opacity-50',
                  'bg-red-600 hover:bg-red-700',
                )}
              >
                {busy ? 'Deleting…' : `Delete ${totalDupCopies} duplicate ${totalDupCopies === 1 ? 'copy' : 'copies'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
